import {
  getDoc,
  getDocFromCache,
  getDocFromServer,
  getDocsFromCache,
  getDocsFromServer,
} from "firebase/firestore";
import { parseSnapshotMetadata } from "./firestoreSnapshotMetadata";

const formatFirestoreReadError = (error) => {
  const code = error?.code ? ` (${error.code})` : "";
  return `${error?.message || "Firestore read failed"}${code}`;
};

/**
 * Read a document: return cached data immediately when present, then always
 * attempt a server read. Server data wins; cache is only used when offline.
 * Falls back to default getDoc when getDocFromServer returns a missing doc.
 *
 * @param {import('firebase/firestore').DocumentReference} docRef
 * @returns {Promise<{ snap: import('firebase/firestore').DocumentSnapshot, freshness: ReturnType<typeof parseSnapshotMetadata>, servedFromCacheOnly: boolean }>}
 */
export const readDocCacheFirstThenServer = async (docRef) => {
  let cacheSnap = null;

  try {
    cacheSnap = await getDocFromCache(docRef);
  } catch {
    // Cache miss — expected on first visit or after eviction.
  }

  try {
    const serverSnap = await getDocFromServer(docRef);
    if (serverSnap.exists()) {
      return {
        snap: serverSnap,
        freshness: parseSnapshotMetadata(serverSnap),
        servedFromCacheOnly: false,
      };
    }

    const defaultSnap = await getDoc(docRef);
    if (defaultSnap.exists()) {
      return {
        snap: defaultSnap,
        freshness: parseSnapshotMetadata(defaultSnap),
        servedFromCacheOnly: defaultSnap.metadata?.fromCache === true,
      };
    }

    return {
      snap: serverSnap,
      freshness: parseSnapshotMetadata(serverSnap),
      servedFromCacheOnly: false,
    };
  } catch (serverError) {
    if (cacheSnap?.exists()) {
      return {
        snap: cacheSnap,
        freshness: parseSnapshotMetadata(cacheSnap),
        servedFromCacheOnly: true,
        serverError,
      };
    }

    try {
      const defaultSnap = await getDoc(docRef);
      if (defaultSnap.exists()) {
        return {
          snap: defaultSnap,
          freshness: parseSnapshotMetadata(defaultSnap),
          servedFromCacheOnly: defaultSnap.metadata?.fromCache === true,
        };
      }
    } catch {
      // Fall through to the server error below.
    }

    throw serverError;
  }
};

/**
 * Read a collection with the same cache-first-then-server policy as {@link readDocCacheFirstThenServer}.
 *
 * @param {import('firebase/firestore').Query|import('firebase/firestore').CollectionReference} queryRef
 */
/**
 * Collection read with server confirmation when online (cache fallback offline).
 * @param {import('firebase/firestore').Query|import('firebase/firestore').CollectionReference} colRef
 */
export const readCollectionServerPreferred = async (colRef) => {
  const { snap } = await readQueryCacheFirstThenServer(colRef);
  return snap;
};

export { formatFirestoreReadError };

export const readQueryCacheFirstThenServer = async (queryRef) => {
  let cacheSnap = null;

  try {
    cacheSnap = await getDocsFromCache(queryRef);
  } catch {
    // Cache miss.
  }

  try {
    const serverSnap = await getDocsFromServer(queryRef);
    return {
      snap: serverSnap,
      freshness: parseSnapshotMetadata(serverSnap),
      servedFromCacheOnly: false,
    };
  } catch (serverError) {
    if (cacheSnap) {
      return {
        snap: cacheSnap,
        freshness: parseSnapshotMetadata(cacheSnap),
        servedFromCacheOnly: true,
        serverError,
      };
    }
    throw serverError;
  }
};

/**
 * Hydrate bootstrap shell from cache only (instant offline startup).
 * Returns null when nothing is cached yet.
 *
 * @param {import('firebase/firestore').DocumentReference} userRef
 * @param {import('firebase/firestore').CollectionReference} classesRef
 */
export const readBootstrapShellFromCache = async (userRef, classesRef) => {
  try {
    const [userSnap, classesSnap] = await Promise.all([
      getDocFromCache(userRef),
      getDocsFromCache(classesRef),
    ]);

    if (!userSnap.exists()) {
      return null;
    }

    return {
      userSnap,
      classesSnap,
      freshness: parseSnapshotMetadata(userSnap),
    };
  } catch {
    return null;
  }
};
